import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { arDigits, fmtLocaleNum } from "@/lib/utils";

// Deterministic plain-language monthly briefing: every sentence is built from canonical DLD aggregates.
const pct = (a, b) => (b ? ((a - b) / b) * 100 : null);

export default function MonthlyBriefing({ isAR }) {
  const [monthly, setMonthly] = useState(null);
  const [areas, setAreas] = useState(null);

  useEffect(() => {
    api.get("/market/monthly-2026").then((r) => setMonthly(r.data)).catch(() => setMonthly(false));
  }, []);

  const complete = (monthly?.months || []).filter((m) => m.status === "reported" && m.month < monthly.current_month);
  const cur = complete[complete.length - 1];
  const prev = complete[complete.length - 2];

  useEffect(() => {
    if (cur?.month) {
      api.get(`/market/monthly-areas?month=${cur.month}`).then((r) => setAreas(r.data)).catch(() => setAreas(false));
    }
  }, [cur?.month]);

  if (!cur || !prev) return null;

  const N = (n) => fmtLocaleNum(n, isAR);
  const B = (bn) => (isAR ? `${arDigits(Number(bn).toFixed(2))} مليار درهم` : `AED ${Number(bn).toFixed(2)}B`);
  const P = (p) => (p == null ? (isAR ? "غير متاح" : "n/a") : (isAR ? `\u2066${arDigits(`${p > 0 ? "+" : ""}${p.toFixed(1)}`)}٪\u2069` : `${p > 0 ? "+" : ""}${p.toFixed(1)}%`));
  const M = (ym) => {
    const s = new Intl.DateTimeFormat(isAR ? "ar-AE" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${ym}-01T00:00:00Z`));
    return isAR ? arDigits(s) : s;
  };
  const cntP = pct(cur.sales_transactions, prev.sales_transactions);
  const valP = pct(cur.sales_aed_bn, prev.sales_aed_bn);
  // R2-MKT-06: exact zero change is described as unchanged, never as a rise/fall.
  const sgn = (p) => { if (p == null) return null; const r = Math.round(p * 10) / 10; return r > 0 ? 1 : r < 0 ? -1 : 0; };
  const cD = sgn(cntP);
  const vD = sgn(valP);
  const vUp = vD > 0;
  const mixed = !!cD && !!vD && cD !== vD;
  const cntAR = { 1: "ارتفع عدد المبيعات المسجّلة", "-1": "انخفض عدد المبيعات المسجّلة", 0: "استقرّ عدد المبيعات المسجّلة دون تغيّر", null: "لا تتوفّر مقارنة لعدد المبيعات" }[cD];
  const valAR = { 1: "ارتفعت القيمة من", "-1": "انخفضت القيمة من", 0: "بقيت القيمة دون تغيّر عند", null: "لا تتوفّر مقارنة للقيمة مع" }[vD];
  const cntEN = { 1: "Registered sales increased", "-1": "Registered sales decreased", 0: "Registered sales were unchanged", null: "No sales-count comparison is available" }[cD];
  const valEN = { 1: "sales value rose from", "-1": "sales value fell from", 0: "sales value was unchanged at", null: "no value comparison is available against" }[vD];

  const what = isAR
    ? `في ${M(cur.month)} سجّلت دائرة الأراضي والأملاك ${N(cur.sales_transactions)} صفقة بيع فعلية بقيمة ${B(cur.sales_aed_bn)}. ${cntAR} مقارنةً بشهر ${M(prev.month)} (${N(prev.sales_transactions)} صفقة، ${P(cntP)})، و${valAR} ${B(prev.sales_aed_bn)} (${P(valP)}).${mixed ? (vUp ? " ارتفعت قيمة الصفقات رغم تسجيل عدد أقل من المبيعات." : " انخفضت قيمة الصفقات رغم تسجيل عدد أكبر من المبيعات.") : ""}`
    : `In ${M(cur.month)}, DLD recorded ${N(cur.sales_transactions)} pure property sales worth ${B(cur.sales_aed_bn)}. ${cntEN} compared with ${M(prev.month)} (${N(prev.sales_transactions)} sales, ${P(cntP)}), and ${valEN} ${B(prev.sales_aed_bn)} (${P(valP)}).${mixed ? (vUp ? " Transaction value increased even though fewer sales were recorded." : " Transaction value decreased even though more sales were recorded.") : ""}`;

  const tc = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s);
  const nm = (r) => (isAR ? r.area_ar || r.area : tc(r.area));
  const where = areas && areas.top_by_count?.length
    ? (isAR
      ? `سُجّل أكبر عدد من المبيعات في: ${areas.top_by_count.slice(0, 3).map((r) => `${nm(r)} (${N(r.sales_count)})`).join("، ")}. وكانت أعلى قيمة مبيعات في: ${areas.top_by_value.slice(0, 3).map((r) => `${nm(r)} (${B(r.sales_aed / 1e9)})`).join("، ")}. سجّلت ${N(areas.areas_up)} منطقة مبيعات أكثر من الشهر السابق، و${N(areas.areas_down)} منطقة مبيعات أقل.`
      : `The most registered sales were recorded in ${areas.top_by_count.slice(0, 3).map((r) => `${nm(r)} (${N(r.sales_count)})`).join(", ")}. The largest sales values were in ${areas.top_by_value.slice(0, 3).map((r) => `${nm(r)} (${B(r.sales_aed / 1e9)})`).join(", ")}. ${N(areas.areas_up)} areas recorded more sales than the previous month, while ${N(areas.areas_down)} recorded fewer.`)
    : null;

  const prior = complete.slice(-4, -1);
  const avg = prior.length === 3 ? prior.reduce((s, m) => s + m.sales_transactions, 0) / 3 : null;
  const avgRaw = avg ? pct(cur.sales_transactions, avg) : null;
  const avgP = avgRaw == null ? null : Math.round(avgRaw * 10) / 10;
  const history = avg
    ? (isAR
      ? `بلغ متوسط المبيعات الشهرية خلال الأشهر الثلاثة المكتملة السابقة (${M(prior[0].month)} إلى ${M(prior[2].month)}) ${N(Math.round(avg))} صفقة. ${avgP === 0 ? `جاءت مبيعات ${M(cur.month)} مساويةً لهذا المتوسط.` : `جاء ${M(cur.month)} ${avgP > 0 ? "أعلى" : "أدنى"} من هذا المتوسط بنسبة ${P(Math.abs(avgP)).replace("+", "")}.`}`
      : `Over the previous three complete months (${M(prior[0].month)} to ${M(prior[2].month)}), DLD recorded an average of ${N(Math.round(avg))} sales per month. ${avgP === 0 ? `${M(cur.month)} was level with that average.` : `${M(cur.month)} was ${P(Math.abs(avgP)).replace("+", "")} ${avgP > 0 ? "above" : "below"} that average.`}`)
    : null;

  const blocks = [
    { id: "what", h: isAR ? "ماذا حدث في سوق دبي العقاري؟" : "What happened in Dubai's property market?", t: what },
    where && { id: "where", h: isAR ? "أين تركّز النشاط؟" : "Where was activity concentrated?", t: where, note: isAR ? "أسماء المناطق هي مناطق التسجيل الرسمية لدى الدائرة. الترتيب يصف النشاط المسجّل فقط، وليس مدى ملاءمة المنطقة للاستثمار." : "Area names are official DLD registration areas. Rankings describe recorded activity only, not investment suitability." },
    history && { id: "history", h: isAR ? "كيف يقارن ذلك بالأشهر الأخيرة؟" : "How does this compare with recent history?", t: history },
  ].filter(Boolean);

  return (
    <section className="mt-10 sm:mt-12" data-testid="insights-monthly-briefing">
      <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-2">
        {isAR ? `الملخّص الشهري · ${M(cur.month)}` : `Monthly briefing · ${M(cur.month)}`}
      </p>
      <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
        {blocks.map((b) => (
          <div key={b.id} className="rounded-2xl border hairline bg-white/70 p-5 sm:p-6" data-testid={`insights-briefing-${b.id}`}>
            <h2 className="font-editorial text-lg sm:text-xl mb-2">{b.h}</h2>
            <p className="text-[14px] text-propertic-ink-2/90 leading-relaxed">{b.t}</p>
            {b.note && <p className="mt-3 text-[11px] text-propertic-muted leading-relaxed">{b.note}</p>}
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] font-mono-brand text-propertic-muted tracking-[0.08em]" data-testid="insights-briefing-source">
        {isAR
          ? `الشهر المعروض: ${M(cur.month)} (شهر مكتمل) · المصدر: مبيعات عقارية مسجّلة لدى دائرة الأراضي والأملاك.`
          : `Month shown: ${M(cur.month)} (complete month) · Source: DLD registered property sales.`}
      </p>
    </section>
  );
}
